// ===========================================================================
// Showroom editor — shared tokens & tiny helpers.
//
// Everything spec-mandated (exact colors, motion, sizes, the bridge message
// protocol) lives here so no raw hex/magic strings are scattered across the
// editor components. Visual tokens map to the app-wide design-system.css
// variables where one exists; the few showroom-specific accents are defined
// once, here.
// ===========================================================================

export const FONT =
  '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Inter", "Helvetica Neue", system-ui, sans-serif';

// --- Design-system variable aliases (used inside inline styles) ------------
export const T = {
  radiusSm: "var(--radius-sm)",
  radiusMd: "var(--radius-md)",
  radiusFrame: "var(--radius-frame)",
  radiusLg: "var(--radius-lg)",
  radiusFull: "var(--radius-full)",
  shadowSm: "var(--shadow-sm)",
  shadowMd: "var(--shadow-md)",
  shadowLg: "var(--shadow-lg)",
  ease: "var(--ds-ease-spring)",
  easeOut: "var(--ds-ease-out)",
  durFast: "var(--ds-dur-fast)",
  durBase: "var(--ds-dur-base)",
  ring: "var(--ds-ring)",
  ink: "var(--slate-900)",
  ink2: "var(--slate-600)",
  ink3: "var(--slate-500)",
  line: "var(--slate-200)",
  line2: "var(--slate-300)",
  surface: "var(--surface-elevated)",
  blue: "var(--blue-700)",
  blueMed: "var(--blue-500)",
  blue50: "var(--blue-50)",
  amber: "var(--status-amber)",
  green: "var(--status-green)",
  red: "var(--status-red)",
};

// Spec-mandated section accent (hover outline + selection). #3EC6FF.
export const EDIT_ACCENT = "#3EC6FF";
export const EDIT_ACCENT_SOFT = "rgba(62,198,255,0.16)"; // 600ms applied-edit flash
export const EDIT_OUTLINE_W = 2; // px
export const EDIT_OUTLINE_RADIUS = 6; // px

// --- Layout constants ------------------------------------------------------
export const RAIL_W = 380; // px — left rail
export const FLASH_MS = 600; // applied-edit flash duration
export const MAX_VERSIONS = 10; // FIFO cap
export const SHARE_TTL_HOURS = 72; // read-only preview link expiry
export const MOBILE_TRAFFIC_THRESHOLD = 60; // % → default to phone view

// Device presets (widths in CSS px of the emulated viewport).
export const DEVICES = [
  { id: "desktop", label: "Desktop", w: null, key: "1" },
  { id: "tablet", label: "Tablet", w: 834, key: "2" },
  { id: "mobile", label: "Phone", w: 390, key: "3" },
];

// --- Bridge protocol -------------------------------------------------------
// The editor (parent) and the injected vella-bridge.js (iframe) speak this
// exact vocabulary. `NONCE`-style validation: the parent sends `init` with a
// per-session token; the bridge echoes it on every outbound message and the
// parent drops anything whose token or origin doesn't match its allowlist.
export const BRIDGE = {
  // parent → bridge
  INIT: "vella:init",
  SET_EDIT: "vella:setEditMode",
  HIGHLIGHT: "highlight",
  FLASH: "flash",
  SCROLL_TO: "scrollTo",
  APPLY_EDIT: "applyEdit",
  REVERT: "revert",
  REVERT_ALL: "revertAll",
  // bridge → parent
  READY: "vella:ready",
  SECTIONS: "sections",
  SECTION_CLICK: "section_click",
  SECTION_HOVER: "section_hover",
};

// The query flag that gates bridge injection on the published site.
export const PREVIEW_FLAG = "vella_preview";

// Validate an inbound postMessage origin against a domain allowlist. The
// allowlist holds full origins (scheme + host [+ port]). window.location's
// own origin is always trusted (same-origin demo previews).
export function isAllowedOrigin(origin, allowlist) {
  if (!origin) return false;
  try {
    if (origin === window.location.origin) return true;
  } catch {
    /* non-browser */
  }
  return Array.isArray(allowlist) && allowlist.includes(origin);
}

// Derive the allowlist for a given preview URL: its own origin plus the app's.
export function originsFor(previewUrl) {
  const out = [];
  try {
    out.push(window.location.origin);
  } catch {
    /* ignore */
  }
  try {
    if (previewUrl) out.push(new URL(previewUrl, window.location.href).origin);
  } catch {
    /* relative / invalid — same-origin covers it */
  }
  return Array.from(new Set(out));
}

// Human date grouping for the history drawer: "Jul 2".
export function dayLabel(ts) {
  try {
    return new Date(ts).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "Earlier";
  }
}

// prefers-reduced-motion — read once, live-updated by useReducedMotion().
export function prefersReducedMotion() {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

// Focus-ring style object (Part 4.6 — one ring token everywhere).
export const focusRing = {
  outline: "none",
  boxShadow: T.ring,
};
